import {
  countDirectoryDescendants,
  deleteDirectoryFromManifest,
  deleteDirectoryRecursiveFromManifest,
  deleteFileFromManifest,
  isDirectoryEmpty,
  moveDirectoryInManifest,
  moveFileInManifest,
  renameDirectoryInManifest,
  renameFileInManifest,
  type SharedFolderActorRef,
  type ManifestCommitResult,
  type ManifestPathConflict,
  type SharedFolderDirectoryEntry,
  type SharedFolderManifestPlaintext,
} from './sharedFolderManifest';
import { commitGuestManifestMutation } from './sharedFolderGuestManifest';

export async function createSharedFolderGuestSubfolder(input: {
  folderToken: string;
  guestInviteId: string;
  folderKey: Uint8Array;
  revision: number;
  manifest: SharedFolderManifestPlaintext;
  currentPath: string;
  folderName: string;
  actor?: SharedFolderActorRef;
}): Promise<ManifestCommitResult> {
  const folderName = input.folderName.trim();
  if (!folderName || folderName.includes('/') || folderName.includes('\\')) {
    throw new Error('invalid_folder_name');
  }

  const path = input.currentPath ? `${input.currentPath}/${folderName}` : folderName;
  const entry: SharedFolderDirectoryEntry = {
    type: 'directory',
    path,
    entryId: crypto.randomUUID(),
    createdAt: new Date().toISOString(),
    createdBy: input.actor,
  };

  return commitGuestManifestMutation(
    input.folderToken,
    input.guestInviteId,
    input.folderKey,
    input.revision,
    input.manifest,
    (latest) => {
      if (latest.entries.some((existing) => existing.path === path)) {
        throw new Error('name_already_in_use');
      }
      return { entries: [...latest.entries, entry] };
    },
  );
}

export async function renameSharedFolderGuestSubfolder(input: {
  folderToken: string;
  guestInviteId: string;
  folderKey: Uint8Array;
  revision: number;
  manifest: SharedFolderManifestPlaintext;
  directoryPath: string;
  newName: string;
}): Promise<ManifestCommitResult> {
  let conflicts: ManifestPathConflict[] = [];

  const result = await commitGuestManifestMutation(
    input.folderToken,
    input.guestInviteId,
    input.folderKey,
    input.revision,
    input.manifest,
    (latest) => {
      const renamed = renameDirectoryInManifest(latest, input.directoryPath, input.newName);
      conflicts = renamed.conflicts;
      return renamed.manifest;
    },
  );

  return { ...result, conflicts };
}

export async function deleteSharedFolderGuestSubfolder(input: {
  folderToken: string;
  guestInviteId: string;
  folderKey: Uint8Array;
  revision: number;
  manifest: SharedFolderManifestPlaintext;
  directoryPath: string;
  recursive: boolean;
}): Promise<ManifestCommitResult> {
  if (!input.recursive && !isDirectoryEmpty(input.manifest, input.directoryPath)) {
    throw new Error('directory_not_empty');
  }

  return commitGuestManifestMutation(
    input.folderToken,
    input.guestInviteId,
    input.folderKey,
    input.revision,
    input.manifest,
    (latest) => (
      input.recursive
        ? deleteDirectoryRecursiveFromManifest(latest, input.directoryPath)
        : deleteDirectoryFromManifest(latest, input.directoryPath)
    ),
  );
}

export async function deleteSharedFolderGuestFile(input: {
  folderToken: string;
  guestInviteId: string;
  folderKey: Uint8Array;
  revision: number;
  manifest: SharedFolderManifestPlaintext;
  filePath: string;
}): Promise<ManifestCommitResult> {
  return commitGuestManifestMutation(
    input.folderToken,
    input.guestInviteId,
    input.folderKey,
    input.revision,
    input.manifest,
    (latest) => deleteFileFromManifest(latest, input.filePath),
  );
}

export async function renameSharedFolderGuestFile(input: {
  folderToken: string;
  guestInviteId: string;
  folderKey: Uint8Array;
  revision: number;
  manifest: SharedFolderManifestPlaintext;
  filePath: string;
  newName: string;
}): Promise<ManifestCommitResult> {
  let conflicts: ManifestPathConflict[] = [];

  const result = await commitGuestManifestMutation(
    input.folderToken,
    input.guestInviteId,
    input.folderKey,
    input.revision,
    input.manifest,
    (latest) => {
      const renamed = renameFileInManifest(latest, input.filePath, input.newName);
      conflicts = renamed.conflicts;
      return renamed.manifest;
    },
  );

  return { ...result, conflicts };
}

export async function moveSharedFolderGuestSubfolder(input: {
  folderToken: string;
  guestInviteId: string;
  folderKey: Uint8Array;
  revision: number;
  manifest: SharedFolderManifestPlaintext;
  directoryPath: string;
  destinationDirectoryPath: string;
}): Promise<ManifestCommitResult> {
  let conflicts: ManifestPathConflict[] = [];

  const result = await commitGuestManifestMutation(
    input.folderToken,
    input.guestInviteId,
    input.folderKey,
    input.revision,
    input.manifest,
    (latest) => {
      const moved = moveDirectoryInManifest(latest, input.directoryPath, input.destinationDirectoryPath);
      conflicts = moved.conflicts;
      return moved.manifest;
    },
  );

  return { ...result, conflicts };
}

export async function moveSharedFolderGuestFile(input: {
  folderToken: string;
  guestInviteId: string;
  folderKey: Uint8Array;
  revision: number;
  manifest: SharedFolderManifestPlaintext;
  filePath: string;
  destinationDirectoryPath: string;
}): Promise<ManifestCommitResult> {
  let conflicts: ManifestPathConflict[] = [];

  const result = await commitGuestManifestMutation(
    input.folderToken,
    input.guestInviteId,
    input.folderKey,
    input.revision,
    input.manifest,
    (latest) => {
      const moved = moveFileInManifest(latest, input.filePath, input.destinationDirectoryPath);
      conflicts = moved.conflicts;
      return moved.manifest;
    },
  );

  return { ...result, conflicts };
}

export function summarizeGuestDirectoryDelete(
  manifest: SharedFolderManifestPlaintext,
  directoryPath: string,
): { files: number; directories: number } {
  return countDirectoryDescendants(manifest, directoryPath);
}
